/**
 * Performs a deep comparison between two _Arrays_ or _Objects_ to determine if they are equivalent.
 * It compares items types, length and properties.
 * @param value The value to compare.
 * @param other The other value to compare.
 * @param unordered Ignores the order when comparing arrays.
 * @returns Returns true if the values are equivalent, else false.
 */
export function isEqual(value: any, other: any, unordered?: boolean): boolean {
  // Compare types
  const type = Object.prototype.toString.call(value);
  if (type !== Object.prototype.toString.call(other)) {
    return false;
  }

  // If items are not an object or array, return false
  if (['[object Array]', '[object Object]'].indexOf(type) < 0) {
    return false;
  }

  // Compare the length of the length of the two items
  const valueLen = type === '[object Array]' ? value.length : Object.keys(value).length;
  const otherLen = type === '[object Array]' ? other.length : Object.keys(other).length;
  if (valueLen !== otherLen) {
    return false;
  }

  // Compare properties
  if (type === '[object Array]') {
    if (unordered) {
      return unorderedArraysEqual(value, other);
    }
    for (let i = 0; i < valueLen; i++) {
      if (compareValues(value[i], other[i]) === false) {
        return false;
      }
    }
  } else {
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        if (compareValues(value[key], other[key]) === false) {
          return false;
        }
      }
    }
  }
  // If nothing failed, return true
  return true;
}

/**
 * Compare items values.
 * @param item1 The first item to compare.
 * @param item2 The other item to compare.
 * @returns Returns true if the item values are the same, else false.
 */
function compareValues(item1: any, item2: any): boolean {
  // Get the object type
  const itemType = Object.prototype.toString.call(item1);
  // If an object or array, compare recursively
  if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
    if (!isEqual(item1, item2)) {
      return false;
    }
  }

  // Otherwise, do a simple comparison
  else {
    // If the two items are not the same type, return false
    if (itemType !== Object.prototype.toString.call(item2)) {
      return false;
    }

    // Else if it's a function, convert to a string and compare
    // Otherwise, just compare
    if (itemType === '[object Function]') {
      if (item1.toString() !== item2.toString()) {
        return false;
      }
    } else {
      if (item1 !== item2) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Compare arrays, unordered.
 * @param array1 The first array to compare.
 * @param array2 The other array to compare.
 * @returns Returns true if the arrays contain the same values, else false.
 */
function unorderedArraysEqual(array1: number[], array2: number[]): boolean {
  if (array1.length !== array2.length) {
    return false;
  }

  let sortedArray1 = array1.concat().sort();
  let sortedArray2 = array2.concat().sort();

  for (let i = 0; i < sortedArray1.length; i++) {
    if (sortedArray1[i] !== sortedArray2[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Get an array of keys for a record type
 * @param object Record to get keys for
 * @returns Keys for the record
 */
export function recordKeys<K extends PropertyKey, T>(object: Record<K, T>) {
  return Object.keys(object) as K[];
}

/**
 * Check if value is a number
 * @param value 
 * @returns true or false
 */
export function isNumber(value: any): boolean {
  return typeof value === "number" && !isNaN(value);
}

/**
 * Convert value to a number if not a number return 0 as default
 */
export function toNumber(value: any): number {
  return isNumber(+value) ? +value : 0;
}
